import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Camera, X } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Z_HELP, panelBottom } from './ReportIssueButton';

/**
 * The help & support menu — owner-approved mockup v2 (2026-09-29), the same
 * layout in platform-app, super-admin-ui and the POS:
 *
 *   ┌ page context ─────────── ✕ ┐   where you are (from the route)
 *   │ [📷] Report a problem       │   the primary action, one tap
 *   │ Tutorials / Record / Ticket │   secondary rows, hidden when unavailable
 *   │ Notifications           (2) │
 *   └ build ─────────────── stage ┘   so a screenshot carries the release
 *
 * Portalled with the launcher into its own top layer (Z_HELP) so it opens
 * above any modal or full-screen editor. Esc closes; arrow keys move focus.
 */

export type HelpMenuSkin = 'dark' | 'light';

export interface HelpMenuItem {
  key: string;
  icon: LucideIcon;
  label: string;
  badge?: number;
  onClick: () => void;
}

interface Props {
  open: boolean;
  onClose: () => void;
  skin: HelpMenuSkin;
  /** Small caps line above the page title, e.g. "Inventory". */
  contextEyebrow?: string;
  contextTitle: string;
  reportSubtitle?: string;
  reportBadge?: number;
  onReport: () => void;
  items: HelpMenuItem[];
  /** Recording strip shown above the primary action. */
  recording?: { steps: number; onOpen: () => void } | null;
  launcherBottom: number;
}

const SKINS: Record<HelpMenuSkin, Record<string, string>> = {
  dark: {
    bg: '#18181b', sunk: '#131315', ink: '#f4f4f5', ink3: '#a1a1aa', line: '#2a2a2e', hover: '#232327',
    pBg: 'linear-gradient(135deg,#DC143C,#9F1239)', pInk: '#ffffff', pIcBg: 'rgba(255,255,255,.14)', pIc: '#ffffff',
    pShadow: '0 6px 16px -6px rgba(220,20,60,.5)', rec: '#fda4af', shadow: '0 18px 40px -12px rgba(0,0,0,.6)',
  },
  light: {
    bg: '#ffffff', sunk: '#faf8f7', ink: '#1c1917', ink3: '#78716c', line: '#ece8e5', hover: '#FFF1F3',
    pBg: 'linear-gradient(135deg,#DC143C,#9F1239)', pInk: '#ffffff', pIcBg: 'rgba(255,255,255,.16)', pIc: '#ffffff',
    pShadow: '0 6px 16px -6px rgba(159,18,57,.55)', rec: '#9F1239', shadow: '0 18px 40px -12px rgba(28,25,23,.35)',
  },
};

const CSS = `
.rt-hm{position:fixed;width:264px;max-width:calc(100vw - 20px);border-radius:14px;overflow:hidden;font-size:13px;line-height:1.35;
  background:var(--hm-bg);color:var(--hm-ink);border:1px solid var(--hm-line);box-shadow:var(--hm-shadow)}
.rt-hm-ctx{display:flex;align-items:center;gap:8px;padding:10px 8px 10px 12px;border-bottom:1px solid var(--hm-line);background:var(--hm-sunk)}
.rt-hm-ctx small{display:block;font-size:10px;font-weight:600;letter-spacing:.07em;text-transform:uppercase;color:var(--hm-ink3)}
.rt-hm-ctx b{display:block;font-size:12.5px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.rt-hm-x{flex:none;width:32px;height:32px;border-radius:8px;border:0;background:transparent;color:var(--hm-ink3);display:flex;align-items:center;justify-content:center;cursor:pointer}
.rt-hm-x:hover{background:var(--hm-hover);color:var(--hm-ink)}
.rt-hm-rec{display:flex;align-items:center;gap:8px;margin:10px 10px 0;padding:8px 10px;border-radius:9px;background:rgba(220,20,60,.12);color:var(--hm-rec);font-size:11.5px;font-weight:600;border:0;width:calc(100% - 20px);cursor:pointer;text-align:left}
.rt-hm-rec i{width:8px;height:8px;border-radius:50%;background:#ef4444;flex:none}
.rt-hm-p{margin:10px;width:calc(100% - 20px);border:0;border-radius:11px;padding:11px 12px;display:flex;gap:11px;align-items:center;cursor:pointer;text-align:left;
  background:var(--hm-pbg);color:var(--hm-pink);box-shadow:var(--hm-pshadow);font:inherit;position:relative}
.rt-hm-p:hover{filter:brightness(1.07)}
.rt-hm-p .ic{width:34px;height:34px;border-radius:9px;flex:none;display:flex;align-items:center;justify-content:center;background:var(--hm-picbg);color:var(--hm-pic)}
.rt-hm-p b{display:block;font-size:13px;font-weight:700}
.rt-hm-p span.s{display:block;font-size:11px;opacity:.8;margin-top:1px}
.rt-hm-list{padding:0 6px 6px;display:flex;flex-direction:column}
.rt-hm-row{display:flex;align-items:center;gap:10px;width:100%;min-height:38px;padding:0 8px;border:0;background:none;color:inherit;font:inherit;border-radius:8px;cursor:pointer;text-align:left}
.rt-hm-row svg{flex:none;color:var(--hm-ink3)}
.rt-hm-row:hover svg,.rt-hm-row:focus-visible svg{color:var(--hm-ink)}
.rt-hm-row .l{flex:1}
.rt-hm-row:hover,.rt-hm-row:focus-visible,.rt-hm-p:focus-visible,.rt-hm-x:focus-visible{outline:none;background-color:var(--hm-hover)}
.rt-hm-p:focus-visible{box-shadow:0 0 0 2px var(--hm-bg),0 0 0 4px #DC143C}
.rt-hm-badge{min-width:18px;height:18px;padding:0 5px;border-radius:9px;font-size:10.5px;font-weight:700;display:inline-flex;align-items:center;justify-content:center;background:#DC143C;color:#fff;font-variant-numeric:tabular-nums}
.rt-hm-p .rt-hm-badge{background:#fff;color:#9F1239;margin-left:auto}
.rt-hm-foot{display:flex;justify-content:space-between;gap:8px;padding:8px 12px;border-top:1px solid var(--hm-line);font:10px ui-monospace,SFMono-Regular,Menlo,monospace;color:var(--hm-ink3)}
.rt-hm-foot .live{display:inline-flex;align-items:center;gap:5px}
.rt-hm-foot .live::before{content:"";width:6px;height:6px;border-radius:50%;background:#10b981}
@media (pointer:coarse){.rt-hm-row{min-height:44px}.rt-hm-x{width:44px;height:44px}}
`;

function readStage(): string {
  try {
    const v = String((import.meta as any)?.env?.VITE_STAGE || '').toLowerCase();
    if (v) return v;
  } catch { /* fall through */ }
  const h = typeof window !== 'undefined' ? window.location.hostname : '';
  if (h.includes('.dev.') || h.startsWith('localhost') || h.startsWith('127.')) return 'dev';
  if (h.includes('stage.')) return 'staging';
  return 'prod';
}

function readBuild(): string {
  const w = typeof window !== 'undefined' ? (window as any) : {};
  const v = w.__BUNDLE_VERSION__ || w.__APP_VERSION__;
  return v ? String(v).slice(0, 16) : 'local build';
}

/** "/pos/products/variant-editor" -> { eyebrow: "Pos", title: "Products › Variant editor" } */
export function describePath(path: string, fallback: string): { eyebrow?: string; title: string } {
  const human = (s: string) => {
    const t = decodeURIComponent(s).replace(/[-_]+/g, ' ').trim();
    return t.charAt(0).toUpperCase() + t.slice(1);
  };
  // Drop ids (uuids, long hex/number runs) so the title stays readable.
  const parts = path.split(/[?#]/)[0].split('/').filter((p) => p && !/^[0-9a-f-]{8,}$/i.test(p) && !/^\d+$/.test(p));
  if (parts.length === 0) return { title: fallback };
  if (parts.length === 1) return { title: human(parts[0]) };
  return { eyebrow: human(parts[0]), title: parts.slice(1).map(human).join(' › ') };
}

const HelpMenu: React.FC<Props> = ({
  open, onClose, skin, contextEyebrow, contextTitle, reportSubtitle, reportBadge, onReport, items, recording, launcherBottom,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    // Focus for keyboard users, without painting a ring on a mouse open.
    (ref.current?.querySelector('.rt-hm-p') as HTMLElement | null)?.focus({ focusVisible: false } as FocusOptions);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const els = Array.from(ref.current?.querySelectorAll('.rt-hm-p,.rt-hm-row') || []) as HTMLElement[];
      if (!els.length) return;
      const i = els.indexOf(document.activeElement as HTMLElement);
      const next = e.key === 'ArrowDown' ? (i + 1) % els.length : (i - 1 + els.length) % els.length;
      els[next].focus();
      e.preventDefault();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  if (!open || typeof document === 'undefined') return null;
  const t = SKINS[skin];
  const vars = {
    '--hm-bg': t.bg, '--hm-sunk': t.sunk, '--hm-ink': t.ink, '--hm-ink3': t.ink3, '--hm-line': t.line, '--hm-hover': t.hover,
    '--hm-pbg': t.pBg, '--hm-pink': t.pInk, '--hm-picbg': t.pIcBg, '--hm-pic': t.pIc, '--hm-pshadow': t.pShadow,
    '--hm-rec': t.rec, '--hm-shadow': t.shadow,
  } as React.CSSProperties;

  return createPortal(
    <>
      <style>{CSS}</style>
      {/* Click-outside catcher — transparent, no native handling. */}
      <div data-help-launcher="" className="fixed inset-0" style={{ zIndex: Z_HELP + 1 }} onClick={onClose} />
      <div
        ref={ref}
        data-help-launcher=""
        role="menu"
        aria-label="Help and support"
        className="rt-hm"
        style={{ ...vars, zIndex: Z_HELP + 2, right: 'max(10px, env(safe-area-inset-right))', bottom: panelBottom(launcherBottom) }}
      >
        <div className="rt-hm-ctx">
          <div style={{ flex: 1, minWidth: 0 }}>
            {contextEyebrow && <small>{contextEyebrow}</small>}
            <b>{contextTitle}</b>
          </div>
          <button type="button" className="rt-hm-x" aria-label="Close" onClick={onClose}><X size={14} /></button>
        </div>

        {recording && (
          <button type="button" className="rt-hm-rec" onClick={recording.onOpen}>
            <i />Recording · {recording.steps} {recording.steps === 1 ? 'step' : 'steps'}
            <span style={{ marginLeft: 'auto', opacity: 0.8 }}>Open</span>
          </button>
        )}

        <button type="button" role="menuitem" className="rt-hm-p" onClick={onReport}>
          <span className="ic"><Camera size={17} /></span>
          <span style={{ minWidth: 0 }}>
            <b>Report a problem</b>
            <span className="s">{reportSubtitle || 'Screenshot, page and recent errors are attached'}</span>
          </span>
          {!!reportBadge && <span className="rt-hm-badge" title="Fixes waiting for you to retest">{reportBadge > 9 ? '9+' : reportBadge}</span>}
        </button>

        {items.length > 0 && (
          <div className="rt-hm-list">
            {items.map(({ key, icon: Icon, label, badge, onClick }) => (
              <button key={key} type="button" role="menuitem" className="rt-hm-row" onClick={onClick}>
                <Icon size={15} />
                <span className="l">{label}</span>
                {!!badge && <span className="rt-hm-badge">{badge > 9 ? '9+' : badge}</span>}
              </button>
            ))}
          </div>
        )}

        <div className="rt-hm-foot">
          <span>{readBuild()}</span>
          <span className="live">{readStage()}</span>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default HelpMenu;
